import { useAuth } from "@/lib/auth";
import { hasSubscriberAccess } from "@/lib/subscription";
import { Loader } from "@/components/ui/Primitives";
import { Link, useLocation } from "react-router-dom";
import { Lock, Sparkles, CheckCircle2, ArrowRight, CreditCard, ShieldCheck } from "lucide-react";

/**
 * LandlordPaywallGate — blocks in-app features for landlords without an
 * active paid subscription. As of Feb 2026 there is no free tier for
 * landlords: they must subscribe with a card before any feature unlocks.
 *
 * Tenants and admins bypass. Every active paid tier, including DIY,
 * unlocks the portal. A canceled subscription's plan name is not access.
 *
 * Whitelisted paths still work while unpaid — pricing/billing/settings
 * so the landlord can actually complete the checkout, plus messages so
 * they can reach us for help.
 */
const WHITELIST = [
  "/service-workspace", // Assigned-job authorization is enforced server-side.
  "/service-requests",
  "/pricing", "/billing", "/settings", "/checkout", "/messages",
  "/role-select", "/logout", "/dashboard/billing", "/admin",
];

export default function LandlordPaywallGate({ children }) {
  const { user, loading } = useAuth() || {};
  const loc = useLocation();
  const path = loc.pathname || "/";

  // Guest / not-logged-in / non-landlord bypass — public pages and
  // tenant dashboards need to load without the gate.
  if (loading) return <Loader label="Loading your account…" />;
  if (!user || user.role !== "landlord") return children;
  if (hasSubscriberAccess(user)) return children;
  if (WHITELIST.some((p) => path === p || path.startsWith(p + "/"))) return children;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="landlord-paywall-gate">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-[0_4px_28px_rgba(15,23,42,0.06)] overflow-hidden">
        <div className="px-6 py-3 bg-emerald-50 text-emerald-800 text-xs font-bold uppercase tracking-[0.18em] border-b border-emerald-100 flex items-center gap-2">
          <Lock className="w-3.5 h-3.5"/> Subscription required
        </div>
        <div className="px-6 sm:px-8 py-8 sm:py-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-[11px] font-bold uppercase tracking-wider">
            {user.name?.split(" ")[0] ? `${user.name.split(" ")[0]} — one quick step` : "One quick step"}
          </div>
          <h1 className="mt-4 font-display text-2xl sm:text-3xl font-black text-slate-950 tracking-tight leading-tight">
            Add your card to unlock every landlord feature.
          </h1>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Central Rentals is a paid platform for Canadian landlords — plans start at <b>CA$35/month</b> (Starter) with portfolio tiers up to <b>CA$80/month</b>. Cancel any time.
          </p>

          <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
            {[
              "Unlimited properties, units, tenants, and leases",
              "RTA-compliant provincial lease generator (all 13 provinces)",
              "Interac + PAD rent collection, CRA T776 exports",
              "PIPEDA-compliant tenant screening (Certn + Equifax CA)",
              "Smart Copilot (Ask Mark), predictive maintenance, message drafts",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 flex-none"/>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/pricing"
              data-testid="paywall-choose-plan"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-600 text-white font-bold hover:bg-emerald-700 shadow-md shadow-emerald-500/25 transition-colors"
            >
              <CreditCard className="w-4 h-4"/> Choose a plan
              <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link
              to="/billing"
              data-testid="paywall-billing"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold transition-colors"
            >
              Manage billing
            </Link>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap items-center gap-4 text-xs text-slate-500">
            <span>Cards processed by Stripe · PCI-DSS Level 1</span>
            <span>·</span>
            <span>Cancel any time</span>
          </div>
        </div>
      </div>
    </div>
  );
}
